import { useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import useThemeStore from "./store/themeStore";
import AuthenticatedRoute from "./components/AuthenticatedRoute";
import { PrimeReactProvider } from "primereact/api";
import Login from "./pages/login";
import Logout from "./pages/logout";
import "./styles/app.scss";
import Players from "./pages/players";
import Notifications from "./pages/notifications/Notifications";
import Tailwind from "primereact/passthrough/tailwind";
import Schedule from "./pages/schedule";
import Teams from "./pages/teams";
import Matches from "./pages/matches";
import Standings from "./pages/standings";
import Awards from "./pages/awards";
import Register from "./pages/register";
import CurrentUser from "./pages/currentUser";
import Main from "./pages/main";
import RegisterSeason from "./pages/notifications/RegisterSeason";
import ScoreCard from "./pages/scoreCard/ScoreCard";
import MobileScoreCard from "./pages/scoreCard/MobileScoreCard";
import GolfScoreCard from "./pages/scoreCard/GolfScoreCard";
import DocumentsForUsers from "./pages/documentsForUser";
import { clarity } from "react-microsoft-clarity";
import { clarityId } from "./constants/constant";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ResetPassword from "./pages/reset/ResetPassword";
import Landing from "./pages/landing/Landing";
import Sponsors from "./pages/sponsors";
import Draft from "./pages/draft";
import PublicTeams from "./pages/public/Teams";
import ConnectivityLabel from "./pages/connectivityLabel/ConnectivityLabel";
import InstallApp from "./pages/installApp/InstallApp";
import Fixtures from "./pages/fixtures";
import AddScores from "./pages/addScores";
import LeaderboardPage from "./pages/leaderboard";
import { TeamDetails } from "./pages/leaderboard/TeamDetails";
import { PlayerDetails } from "./pages/leaderboard/PlayerDetails";
import PublicLeaderboard, {
  PublicFixtures,
  PublicPlayerDetails,
  PublicTeamDetails,
} from "./pages/leaderboard/PublicLeaderboard";
import { LeaderboardCurrentMatch } from "./pages/leaderboard/LeaderboardCurrentMatch";

const App: React.FC = () => {
  clarity.init(clarityId);
  const theme = useThemeStore((state) => state.theme);
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    console.log(`Application Version: ${__APP_VERSION__}`);
  }, [theme]);
  return (
    <PrimeReactProvider value={{ unstyled: true, pt: Tailwind }}>
      <div className={theme}>
        <InstallApp />
        <ConnectivityLabel />
        <Router>
          <Routes>
            <Route
              path="/currentSeason/players"
              element={<AuthenticatedRoute element={<Players />} />}
            />
            <Route
              path="/currentSeason/schedule"
              element={<AuthenticatedRoute element={<Schedule />} />}
            />
            <Route
              path="/currentSeason/teams"
              element={<AuthenticatedRoute element={<Teams />} />}
            />
            <Route
              path="/currentSeason/groups"
              element={<AuthenticatedRoute element={<Teams />} />}
            />
            <Route
              path="/currentSeason/matches"
              element={<AuthenticatedRoute element={<Matches />} />}
            />
            <Route
              path="/currentSeason/standings"
              element={<AuthenticatedRoute element={<Standings />} />}
            />
            <Route
              path="/currentSeason/awards"
              element={<AuthenticatedRoute element={<Awards />} />}
            />
            <Route
              path="/currentSeason/docs"
              element={<AuthenticatedRoute element={<DocumentsForUsers />} />}
            />
            <Route
              path="/currentSeason/sponsors"
              element={<AuthenticatedRoute element={<Sponsors />} />}
            />

            <Route
              path="/currentSeason/draft"
              element={<AuthenticatedRoute element={<Draft />} />}
            />
            <Route
              path="/notifications"
              element={<AuthenticatedRoute element={<Notifications />} />}
            />
            <Route
              path="/registerForSeason"
              element={<AuthenticatedRoute element={<RegisterSeason />} />}
            />

            <Route
              path="/currentUser/details"
              element={<AuthenticatedRoute element={<CurrentUser />} />}
            />
            <Route
              path="currentSeason/fixtures"
              element={<AuthenticatedRoute element={<Fixtures />} />}
            />
            <Route
              path="currentSeason/addScores"
              element={<AuthenticatedRoute element={<AddScores />} />}
            />

            <Route
              path="currentSeason/leaderboard"
              element={<AuthenticatedRoute element={<LeaderboardPage />} />}
            />
            <Route
              path="currentSeason/leaderboard/player/:playerId"
              element={<AuthenticatedRoute element={<PlayerDetails />} />}
            />
            <Route
              path="currentSeason/leaderboard/team/:teamId"
              element={<AuthenticatedRoute element={<TeamDetails />} />}
            />

            <Route path="/" element={<Landing />} />
            <Route path="/teams" element={<PublicTeams />} />
            <Route
              path="/invite/*"
              element={<AuthenticatedRoute element={<Main />} />}
            />
            <Route path="/auth/resetPassword" element={<ResetPassword />} />
            <Route path="/login" element={<Login />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/register" element={<Register />} />
            <Route path="/scoreCard" element={<ScoreCard />} />
            <Route path="/MobileScoreCard" element={<MobileScoreCard />} />
            <Route path="/GolfScoreCard" element={<GolfScoreCard />} />
            <Route path="/leaderboard" element={<PublicLeaderboard />} />

            <Route
              path="leaderboard/player/:playerId"
              element={<PublicPlayerDetails />}
            />
            <Route
              path="leaderboard/team/:teamId"
              element={<PublicTeamDetails />}
            />

            <Route path="/leaderboard/currentMatch" element={<LeaderboardCurrentMatch />} />

            <Route path="/fixtures" element={<PublicFixtures />} />
          </Routes>
        </Router>
        <ToastContainer />
      </div>
    </PrimeReactProvider>
  );
};

export default App;
